/**
 * WhatsApp Embedded Signup — pure message-event parsing for the
 * `/whatsapp-signup` page (2026-08-25, whatsapp-embedded-signup-page).
 *
 * Meta's popup posts progress back to the opener via `window.postMessage`.
 * The page listens for `message` events and feeds `event.origin` +
 * `event.data` through {@link parseEmbeddedSignupMessage}; everything
 * DOM-free lives here so the parsing is unit-testable (the page itself is
 * smoke-tested, not vitest-tested, per the web-tree convention).
 *
 * Shapes (Meta's live docs 2026-08-25; the coexistence finish also as the
 * 2026-10-27 `clavis` run sent it):
 *
 *   Standard ES finish:
 *     {type: "WA_EMBEDDED_SIGNUP", event: "FINISH",
 *      data: {phone_number_id, waba_id, business_id}}
 *   Coexistence finish (the Business-app number path). Meta's page shows
 *   only `waba_id`; the 2026-10-27 `clavis` finish carried
 *   `phone_number_id` and `business_id` too, plus empty `page_ids`,
 *   `catalog_ids`, `dataset_ids` and `instagram_account_ids`, and no
 *   `version`. Only `waba_id` is required: the backend lists the WABA's
 *   numbers either way and verifies a supplied `phone_number_id` against
 *   that list:
 *     {type: "WA_EMBEDDED_SIGNUP",
 *      event: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
 *      data: {waba_id, phone_number_id?, business_id?, …}}
 *   Abandon:
 *     {type: "WA_EMBEDDED_SIGNUP", event: "CANCEL",
 *      data: {current_step}}
 *
 * A message may also carry a `version` field (Meta's docs show 3; the
 * 2026-10-27 finish had none). This parser deliberately does NOT branch on
 * it — it matches on `type`, the event name and `data.waba_id`, so a
 * session-info version bump alone does not break it. What WOULD break it is a renamed event or a moved
 * `waba_id`; that is exactly what `{kind: "unrecognised"}` exists to make
 * visible (2026-10-11, the Embedded Signup v2 -> v4 question).
 */

export type EmbeddedSignupMessage =
  | {
      readonly kind: "finish";
      /** Which finish variant fired (coexistence carries its own name). */
      readonly event: string;
      readonly wabaId: string;
      readonly phoneNumberId: string | null;
    }
  | { readonly kind: "cancel"; readonly currentStep: string | null }
  /**
   * A WA_EMBEDDED_SIGNUP message from a facebook.com origin that this
   * parser could NOT classify — an unknown event name, or a finish-family
   * event whose `waba_id` is missing or malformed. Surfaced rather than
   * dropped so a shape change (e.g. a future Embedded Signup version
   * renaming the finish event) is VISIBLE instead of hanging the page on a
   * popup that never appears to finish.
   */
  | {
      readonly kind: "unrecognised";
      /**
       * The event name. Always present: a WA_EMBEDDED_SIGNUP message whose
       * `event` is not a string carries nothing diagnostic to report, so it
       * stays `ignored` rather than becoming an unrecognised finding.
       */
      readonly event: string;
      /** Top-level keys of `data` — NAMES ONLY, never values (shape, not PII). */
      readonly dataKeys: readonly string[];
    }
  | { readonly kind: "ignored" };

/** Every FINISH-family event name the popup can emit. */
const FINISH_EVENTS = new Set([
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
]);

/**
 * Classify one `message` event. Returns `{kind: "ignored"}` for anything
 * that is not a well-formed WA_EMBEDDED_SIGNUP message from a facebook.com
 * origin — including FINISH payloads with no usable `waba_id`, which are
 * ignored rather than surfaced as a broken finish.
 *
 * `data` arrives either as an object or as a JSON string depending on the
 * SDK build; both are accepted.
 */
export function parseEmbeddedSignupMessage(
  origin: string,
  data: unknown,
): EmbeddedSignupMessage {
  if (!originIsFacebook(origin)) return { kind: "ignored" };
  let parsed: unknown = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return { kind: "ignored" };
    }
  }
  if (parsed === null || typeof parsed !== "object") {
    return { kind: "ignored" };
  }
  const obj = parsed as Record<string, unknown>;
  if (obj["type"] !== "WA_EMBEDDED_SIGNUP") return { kind: "ignored" };
  const event = obj["event"];
  if (typeof event !== "string") return { kind: "ignored" };
  const payload =
    obj["data"] !== null && typeof obj["data"] === "object"
      ? (obj["data"] as Record<string, unknown>)
      : {};
  if (FINISH_EVENTS.has(event)) {
    const wabaId = payload["waba_id"];
    if (typeof wabaId !== "string" || !/^\d{1,32}$/.test(wabaId)) {
      // A finish we cannot act on. Previously dropped silently, which left
      // the page waiting forever with no diagnostic.
      return { kind: "unrecognised", event, dataKeys: Object.keys(payload) };
    }
    const phoneNumberId = payload["phone_number_id"];
    return {
      kind: "finish",
      event,
      wabaId,
      phoneNumberId:
        typeof phoneNumberId === "string" && /^\d{1,32}$/.test(phoneNumberId)
          ? phoneNumberId
          : null,
    };
  }
  if (event === "CANCEL") {
    const step = payload["current_step"];
    return {
      kind: "cancel",
      currentStep: typeof step === "string" ? step : null,
    };
  }
  // Right type, right origin, unknown event — report the shape.
  return { kind: "unrecognised", event, dataKeys: Object.keys(payload) };
}

/**
 * Meta's documented origin check: the popup posts from a facebook.com
 * host (www.facebook.com / web.facebook.com / …). Suffix-match the HOST,
 * not the raw origin string — `https://evilfacebook.com` must fail while
 * `https://web.facebook.com` passes.
 */
export function originIsFacebook(origin: string): boolean {
  let host: string;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }
  return host === "facebook.com" || host.endsWith(".facebook.com");
}

/**
 * What a failed Activate press tells the operator, by the server's stable
 * `code` (2026-10-27, wa-subscribe-at-activate). The page gets only the
 * code — `authedPost` drops the response body — and each code means
 * something different for production: whether the app now hears the new
 * number, whether this press wrote anything, and whether pressing again
 * finishes the job. An unknown code claims none of that.
 */
export function describeActivateFailure(code: string | null): string {
  switch (code) {
    case "whatsapp_signup_promote_failed":
      return "The app IS now subscribed to the new number's WABA, but the live rows may not have been fully switched, so sends may fail or leave from the old number until they are. Press Activate again now.";
    case "whatsapp_signup_subscribe_failed":
      return "Meta refused to subscribe the app to the new WABA, for example because the parked token expired. Nothing was written by this press and the parked signup is kept.";
    case "whatsapp_signup_subscribe_unconfirmed":
      return "Meta did not answer the subscribe call, so it may or may not have applied. If it did, messages to the new number already reach the AI while its replies still leave from the old number. Nothing was written by this press and the parked signup is kept. Press Activate again now.";
    case "whatsapp_signup_nothing_pending":
      return "No complete parked signup was found. If an earlier press finished, production is already repointed; otherwise run the signup again.";
    case "whatsapp_signup_templates_not_ready":
      return "Some templates this studio sends are not APPROVED on the new WABA yet; the table above lists them. Nothing was subscribed or written. Re-create them there (runbook §7), wait until they are APPROVED, and press Activate again.";
    case "whatsapp_signup_template_check_failed":
      return "The new WABA's templates could not be read from Meta, so Activate did not go ahead. Nothing was subscribed or written; the parked signup is kept. Try again in a minute.";
    case "whatsapp_signup_template_inventory_unavailable":
      return "The list of templates this studio sends could not be built (its flows or config could not be read), so Activate did not go ahead. Nothing was subscribed or written; the parked signup is kept.";
    default:
      return `Activation failed (${code ?? "no code"}). Reload the page to see the current state before pressing again.`;
  }
}

/**
 * What a failed template check tells the operator (wa-activate-template-check,
 * 2026-10-28). The check writes nothing, so no failure here changes
 * production; Activate stays locked until a check comes back ready.
 */
export function describeTemplateCheckFailure(code: string | null): string {
  switch (code) {
    case "whatsapp_signup_nothing_pending":
      return "No parked signup was found, so there is no new WABA to check.";
    case "whatsapp_signup_template_check_failed":
      return "Meta did not list the new WABA's templates (an expired parked token, or Meta did not answer). Activate stays locked; check again in a minute.";
    case "whatsapp_signup_template_inventory_unavailable":
      return "The list of templates this studio sends could not be built (its flows or config could not be read). Activate stays locked until it can be.";
    default:
      return `The template check failed (${code ?? "no code"}). Activate stays locked; check again.`;
  }
}

/** One template's state on the new WABA, as the page's table shows it. */
export interface TemplateRowState {
  readonly text: string;
  readonly tone: "success" | "warn" | "error";
}

/**
 * The new-WABA cell of one template row. Only APPROVED is sendable; a template
 * Meta is still reviewing is amber, anything else (missing, rejected, paused,
 * disabled) red.
 */
export function templateRowState(row: {
  readonly new: { readonly status: string; readonly category: string } | null;
}): TemplateRowState {
  if (row.new === null) return { text: "Missing", tone: "error" };
  const status = row.new.status.toUpperCase();
  if (status === "APPROVED") {
    return { text: `APPROVED · ${row.new.category}`, tone: "success" };
  }
  if (status === "PENDING" || status === "IN_APPEAL") {
    return { text: `${status} · ${row.new.category}`, tone: "warn" };
  }
  return { text: status, tone: "error" };
}

/**
 * The category note for one row: said only when both accounts hold the
 * template and Meta filed it differently on the new one (a re-creation is
 * classified afresh, and since October 2026 Meta files generic staff templates
 * as MARKETING). Never blocking: accepting a category is the operator's call.
 */
export function templateCategoryNote(row: {
  readonly new: { readonly category: string } | null;
  readonly live: { readonly category: string } | null;
  readonly category_changed: boolean;
}): string | null {
  if (!row.category_changed || row.new === null || row.live === null) return null;
  return `today ${row.live.category}, new account ${row.new.category}`;
}
